import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import AdminLayout from '../components/AdminLayout';

const CUISINES = [
  'Indian', 'Chinese', 'Japanese', 'Mexican', 'Italian',
  'Mediterranean', 'American', 'Thai', 'Korean', 'Middle Eastern',
  'Vietnamese', 'Greek', 'French', 'Spanish', 'Ethiopian', 'Other'
];

export default function Setup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=info, 2=upload, 3=processing, 4=done
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileRef = React.useRef();

  const [form, setForm] = useState({
  name: '',
  cuisine_type: '',
  phone: '',
  address: '',
  city: '',
  spice_range: '1-5',
  });

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleInfoSubmit = async () => {
    if (!form.name.trim()) { setError('Restaurant name is required.'); return; }
    if (!form.cuisine_type) { setError('Please select a cuisine type.'); return; }
    setError('');

        // Save restaurant info to settings
    try {
      const rests = await api.get('/restaurants');
      let rid;
      if (!rests || rests.length === 0) {
        // Create restaurant for new user
        const newRest = await api.post('/restaurants', {
          name: form.name,
          cuisine_type: form.cuisine_type,
          phone: form.phone || null,
          address: form.address || null,
          city: form.city || null,
        });
        rid = newRest.restaurant_id;
      } else {
        rid = rests[0].restaurant_id;
        await api.put(`/restaurants/${rid}`, {
          name: form.name,
          cuisine_type: form.cuisine_type,
          phone: form.phone || null,
          address: form.address || null,
          city: form.city || null,
        });
      }
      setStep(2);
    } catch (e) {
      setError(e.message || 'Failed to save. Please try again.');
    }
  };

   const handleFiles = async (files) => {
    if (!files.length) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    for (const file of files) {
      if (!allowed.includes(file.type)) {
        setError(`Invalid file type: ${file.name}. Use PDF, JPEG, PNG or WebP.`);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError(`File too large: ${file.name}. Max size is 10MB.`);
        return;
      }
    }
    setError('');
    setLoading(true);
    setStep(3);

    try {
      const rests = await api.get('/restaurants');
      if (!rests || rests.length === 0) throw new Error('No restaurant found.');
      const rid = rests[0].restaurant_id;

      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('cuisine_type', form.cuisine_type);
      formData.append('spice_range', form.spice_range);

      const token = sessionStorage.getItem('qrmenu_token');
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/restaurants/${rid}/setup/upload-menu`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed.');
      setResult(data);
      setStep(4);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    border: '1.5px solid #e5e7eb', fontSize: 14, outline: 'none',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block'
  };

  return (
    <AdminLayout>
      <div style={{ maxWidth: 560, margin: '40px auto', padding: '0 24px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🍽️</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#1a1a1a', marginBottom: 8 }}>
            Welcome! Let's build your menu
          </h1>
          <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>
            Get your digital menu ready in minutes
          </p>

          {/* Step indicators */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
            {[1, 2, 3].map(s => (
              <React.Fragment key={s}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  background: step >= s ? '#1a5c2a' : '#e5e7eb',
                  color: step >= s ? 'white' : '#999'
                }}>{s}</div>
                {s < 3 && <div style={{ width: 40, height: 2, background: step > s ? '#1a5c2a' : '#e5e7eb' }} />}
              </React.Fragment>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 32, marginTop: 6 }}>
            <span style={{ fontSize: 11, color: step >= 1 ? '#1a5c2a' : '#999' }}>Restaurant Info</span>
            <span style={{ fontSize: 11, color: step >= 2 ? '#1a5c2a' : '#999' }}>Upload Menu</span>
            <span style={{ fontSize: 11, color: step >= 3 ? '#1a5c2a' : '#999' }}>Done</span>
          </div>
        </div>

        {/* Step 1 — Restaurant Info */}
        {step === 1 && (
          <div style={{ background: 'white', borderRadius: 16, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 20, color: '#1a1a1a' }}>
              Tell us about your restaurant
            </h2>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Restaurant Name *</label>
              <input style={inputStyle} placeholder="e.g. Golden Dragon Restaurant"
                value={form.name} onChange={e => setF('name', e.target.value)} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Cuisine Type *</label>
              <select style={inputStyle} value={form.cuisine_type} onChange={e => setF('cuisine_type', e.target.value)}>
                <option value="">Select cuisine type</option>
                {CUISINES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Phone Number</label>
              <input
                style={inputStyle}
                placeholder="e.g. +1 (555) 123-4567"
                value={form.phone}
                onChange={e => {
                  const val = e.target.value.replace(/[^\d+\-\s()]/g, '');
                  if (val.replace(/\D/g, '').length <= 15) setF('phone', val);
                }}
                maxLength={20}
              />
              <p style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Include country code (e.g. +1 for US, +91 for India)</p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Address</label>
              <input style={inputStyle} placeholder="e.g. 123 Main Street"
                value={form.address} onChange={e => setF('address', e.target.value)} />
            </div>

                        <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} placeholder="e.g. San Francisco, CA"
                value={form.city} onChange={e => setF('city', e.target.value)} />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Spice Range</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { value: '1-3', label: '😊 Mild (1-3)' },
                  { value: '1-5', label: '🌶️ Medium (1-5)' },
                  { value: '1-7', label: '🔥 Spicy (1-7)' },
                  { value: '1-10', label: '🔥🔥 Extra Hot (1-10)' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setF('spice_range', opt.value)}
                    style={{
                      padding: '8px 14px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      border: `2px solid ${form.spice_range === opt.value ? '#1a5c2a' : '#e5e7eb'}`,
                      background: form.spice_range === opt.value ? '#f0fdf4' : 'white',
                      color: form.spice_range === opt.value ? '#1a5c2a' : '#555',
                      cursor: 'pointer'
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#b91c1c', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
            )}

            <button
              onClick={handleInfoSubmit}
              style={{ width: '100%', padding: '12px', background: '#1a5c2a', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
            >
              Next → Upload Menu
            </button>

            <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#aaa' }}>
              <span onClick={() => navigate('/admin')} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
                Skip for now
              </span>
            </p>
          </div>
        )}

                {/* Step 2 — Upload Menu */}
        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>
                🤖 Upload your menu
                <span style={{ marginLeft: 8, fontSize: 11, background: '#1a5c2a', color: 'white', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>AI POWERED</span>
              </h2>
              <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
                Add all your menu images below, then click "Create Menu".
              </p>

              {/* Selected files list */}
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {selectedFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>🖼️</span>
                        <span style={{ fontSize: 13, color: '#166534' }}>{f.name}</span>
                      </div>
                      <button onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add image button */}
              <button
                onClick={() => fileRef.current.click()}
                style={{ width: '100%', padding: '12px', background: '#f0fdf4', border: '2px dashed #4ade80', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#1a5c2a', cursor: 'pointer', marginBottom: 12 }}
              >
                + Add Menu Image or PDF
              </button>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
                style={{ display: 'none' }} onChange={e => {
                  const newFiles = Array.from(e.target.files);
                  setSelectedFiles(prev => [...prev, ...newFiles]);
                  e.target.value = '';
                }} />

              <p style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>
                Supports PDF, JPEG, PNG, WebP · Max 10MB per file
              </p>

              {/* Create menu button */}
              <button
                onClick={() => selectedFiles.length > 0 && handleFiles(selectedFiles)}
                disabled={selectedFiles.length === 0}
                style={{ width: '100%', padding: '14px', background: selectedFiles.length > 0 ? '#1a5c2a' : '#ccc', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: selectedFiles.length > 0 ? 'pointer' : 'not-allowed' }}
              >
                {selectedFiles.length === 0 ? 'Add at least 1 image to continue' : `🤖 Create Menu from ${selectedFiles.length} image${selectedFiles.length > 1 ? 's' : ''}`}
              </button>
            </div>

            {/* Manual option */}
            <div
              onClick={() => navigate('/admin/menu')}
              style={{ border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px', textAlign: 'center', cursor: 'pointer', background: 'white' }}
            >
              <div style={{ fontSize: 28, marginBottom: 6 }}>✏️</div>
              <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>Build Manually</h2>
              <p style={{ fontSize: 13, color: '#888' }}>Add categories and menu items one by one</p>
            </div>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 16px', color: '#b91c1c', fontSize: 14 }}>
                {error}
              </div>
            )}

            <p style={{ textAlign: 'center', fontSize: 13, color: '#aaa' }}>
              <span onClick={() => setStep(1)} style={{ cursor: 'pointer', textDecoration: 'underline' }}>← Back</span>
            </p>
          </div>
        )}

        {/* Step 3 — Processing */}
        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <div style={{ fontSize: 48, marginBottom: 24, display: 'inline-block', animation: 'spin 1s linear infinite' }}>⚙️</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 12 }}>
              AI is building your menu...
            </h2>
            <p style={{ fontSize: 15, color: '#666', lineHeight: 1.6 }}>
              This takes 15–30 seconds.<br />
              Creating your categories and menu items automatically.
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Step 4 — Done */}
        {step === 4 && result && (
          <div style={{ textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a1a', marginBottom: 12 }}>
              Your menu is ready!
            </h2>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '20px 24px', marginBottom: 28 }}>
              <p style={{ fontSize: 15, color: '#166534', margin: 0, lineHeight: 1.8 }}>
                ✅ <strong>{result.categories_created}</strong> categories created<br />
                ✅ <strong>{result.items_created}</strong> menu items created
              </p>
            </div>
            <p style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
              Review your menu items, add photos, and download your QR code.
            </p>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '16px 20px', marginBottom: 20, textAlign: 'left' }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>📝 Add more restaurant details</p>
              <p style={{ fontSize: 13, color: '#b45309', margin: 0 }}>Add your opening hours, Google Maps link, social media links and cover photos to complete your profile.</p>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/admin/settings')}
                style={{ background: '#1a5c2a', color: 'white', padding: '12px 24px', borderRadius: 10, fontSize: 15, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
                ⚙️ Complete Restaurant Profile
              </button>
              <button onClick={() => navigate('/admin/menu')}
                style={{ background: 'white', color: '#1a5c2a', padding: '12px 24px', borderRadius: 10, fontSize: 15, fontWeight: 600, border: '1.5px solid #1a5c2a', cursor: 'pointer' }}>
                ✏️ Review Menu Items
                </button>
                <button onClick={() => navigate('/admin')}
                style={{ background: 'white', color: '#666', padding: '12px 24px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: '1.5px solid #e5e7eb', cursor: 'pointer' }}>
                Skip for now
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}